import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { EmailHealthCard } from '@/components/EmailHealthCard';
import {
  Shield, Users, BarChart3, FileText, Database, BookOpen, Bell,
  Activity, Server, Save, CheckCircle2, Loader2, Settings2, Trash2, Plus, Pencil,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="glass-card rounded-2xl p-4 flex items-center gap-3">
      <div className={`p-2.5 rounded-xl ${color} border`}>
        <Icon className="w-5 h-5" strokeWidth={1.5} />
      </div>
      <div>
        <p className="text-xl font-heading font-bold text-foreground">{value}</p>
        <p className="text-[10px] text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [health, setHealth] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exploreStats, setExploreStats] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [rlsPolicies, setRlsPolicies] = useState([]);
  const [rlsFormOpen, setRlsFormOpen] = useState(false);
  const [editingRls, setEditingRls] = useState(null);
  const [rlsForm, setRlsForm] = useState({ name: '', model_id: '', field: '', operator: 'equals', value_source: 'static', value: '', user_attribute: '', roles: '' });
  const [models, setModels] = useState([]);
  const [branding, setBranding] = useState({});
  const [newKeyName, setNewKeyName] = useState('');
  // One-time plaintext key surface — set right after creation, cleared on
  // copy or after the user clicks "Done". The server never reveals it again.
  const [justCreatedKey, setJustCreatedKey] = useState(null);
  // Picker for the "Connect from Excel/Power BI" helper that builds the
  // exact OData URL for a specific model.
  const [odataModelId, setOdataModelId] = useState('');
  const [odataModelOptions, setOdataModelOptions] = useState([]);
  const [adminTab, setAdminTab] = useState('config');

  useEffect(() => {
    Promise.all([api.getSystemHealth(), api.getTenantConfig(), api.exploreAnalytics().catch(() => null), api.getAuditLogs().catch(() => []), api.listApiKeys().catch(() => []), api.listRlsPolicies().catch(() => []), api.getBranding().catch(() => ({})), api.getSemanticModels().catch(() => [])])
      .then(([h, c, ea, al, ak, rls, br, mods]) => {
        setHealth(h); setConfig(c); setExploreStats(ea); setAuditLogs(al);
        // Secure path returns {keys:[...]}; tolerate the legacy plain array.
        const keysArr = Array.isArray(ak) ? ak : (ak?.keys || []);
        setApiKeys(keysArr); setRlsPolicies(rls); setBranding(br);
        const ms = Array.isArray(mods) ? mods : (mods?.models || []);
        setOdataModelOptions(ms);
        if (ms.length) setOdataModelId(prev => prev || ms[0].model_id);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
    api.getSemanticModels().then(m => setModels(m || [])).catch(() => {});
  }, []);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.updateTenantConfig(config);
      toast.success('Configuration saved');
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-6"><div className="glass-card rounded-2xl p-6 h-40 animate-pulse" /></div>;

  return (
    <div className="p-6 space-y-6 max-w-5xl" data-testid="admin-page">
      <div className="animate-slide-up">
        <h1 className="text-3xl font-heading font-bold tracking-tight text-foreground">Admin Console</h1>
        <p className="text-sm text-muted-foreground mt-1">Tenant administration, system health, and security policies</p>
      </div>

      <div className="flex gap-1 border-b border-border/50">
        {[['config', 'Configuration'], ['security', 'Security'], ['api_keys', 'API Keys'], ['audit', 'Audit Log'], ['branding', 'Branding']].map(([key, label]) => (
          <button key={key} onClick={() => setAdminTab(key)} className={`px-4 py-2 text-xs font-semibold transition-colors ${adminTab === key ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}>{label}</button>
        ))}
      </div>

      {adminTab === 'config' && <>
      <EmailHealthCard />
      {/* System Health */}
      {health && (
        <div className="space-y-3 animate-slide-up" style={{ animationDelay: '50ms' }}>
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-primary" />
            <h2 className="text-base font-heading font-semibold text-foreground">System Health</h2>
            <Badge className="text-[9px] bg-emerald-500/15 text-emerald-400 border-emerald-500/20">
              <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> {health.status}
            </Badge>
            <Badge variant="outline" className="text-[9px] ml-auto">v{health.platform_version}</Badge>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="health-stats">
            <StatCard label="Users" value={health.users} icon={Users} color="bg-blue-500/10 border-blue-500/20 text-blue-400" />
            <StatCard label="Dashboards" value={health.dashboards} icon={BarChart3} color="bg-indigo-500/10 border-indigo-500/20 text-indigo-400" />
            <StatCard label="Reports" value={health.reports} icon={FileText} color="bg-emerald-500/10 border-emerald-500/20 text-emerald-400" />
            <StatCard label="Data Sources" value={health.data_sources} icon={Database} color="bg-amber-500/10 border-amber-500/20 text-amber-400" />
            <StatCard label="Glossary Terms" value={health.glossary_terms} icon={BookOpen} color="bg-purple-500/10 border-purple-500/20 text-purple-400" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="glass-card rounded-xl p-3 text-center">
              <p className="text-lg font-heading font-bold text-foreground">{health.total_queries}</p>
              <p className="text-[9px] text-muted-foreground">Queries Executed</p>
            </div>
            <div className="glass-card rounded-xl p-3 text-center">
              <p className="text-lg font-heading font-bold text-foreground">{health.total_ai_chats}</p>
              <p className="text-[9px] text-muted-foreground">AI Conversations</p>
            </div>
            <div className="glass-card rounded-xl p-3 text-center">
              <p className="text-lg font-heading font-bold text-foreground">{health.active_alerts}</p>
              <p className="text-[9px] text-muted-foreground">Active Alerts</p>
            </div>
          </div>
        </div>
      )}

      {/* Security Policy Config (E3-S3) */}
      {config && (
        <div className="glass-card rounded-2xl p-6 space-y-5 animate-slide-up" style={{ animationDelay: '100ms' }}>
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-primary" />
            <h2 className="text-base font-heading font-semibold text-foreground">Security Policies</h2>
          </div>
          <Separator className="bg-border/50" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Session */}
            <div className="space-y-3">
              <p className="text-xs font-heading font-semibold text-foreground">Session & Auth</p>
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Session Timeout (minutes)</Label>
                <Input type="number" value={config.session_timeout_minutes || 480} onChange={e => setConfig(c => ({ ...c, session_timeout_minutes: Number(e.target.value) }))}
                  className="w-24 h-7 text-xs rounded-lg bg-muted/50 border-border/50 text-right" data-testid="config-session-timeout" />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Require MFA</Label>
                <Switch checked={config.mfa_required || false} onCheckedChange={v => setConfig(c => ({ ...c, mfa_required: v }))} data-testid="config-mfa-required" />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-3">
              <p className="text-xs font-heading font-semibold text-foreground">Password Policy</p>
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Min Length</Label>
                <Input type="number" value={config.password_min_length || 8} onChange={e => setConfig(c => ({ ...c, password_min_length: Number(e.target.value) }))}
                  className="w-24 h-7 text-xs rounded-lg bg-muted/50 border-border/50 text-right" data-testid="config-password-length" />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Require Special Characters</Label>
                <Switch checked={config.password_require_special || false} onCheckedChange={v => setConfig(c => ({ ...c, password_require_special: v }))} data-testid="config-password-special" />
              </div>
            </div>

            {/* Query & Export */}
            <div className="space-y-3">
              <p className="text-xs font-heading font-semibold text-foreground">Query & Export Limits</p>
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Max Query Rows</Label>
                <Input type="number" value={config.max_query_rows || 10000} onChange={e => setConfig(c => ({ ...c, max_query_rows: Number(e.target.value) }))}
                  className="w-24 h-7 text-xs rounded-lg bg-muted/50 border-border/50 text-right" data-testid="config-max-query-rows" />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Max Export Rows</Label>
                <Input type="number" value={config.max_export_rows || 50000} onChange={e => setConfig(c => ({ ...c, max_export_rows: Number(e.target.value) }))}
                  className="w-24 h-7 text-xs rounded-lg bg-muted/50 border-border/50 text-right" />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Export Enabled</Label>
                <Switch checked={config.export_enabled !== false} onCheckedChange={v => setConfig(c => ({ ...c, export_enabled: v }))} data-testid="config-export-enabled" />
              </div>
            </div>

            {/* Features */}
            <div className="space-y-3">
              <p className="text-xs font-heading font-semibold text-foreground">Platform Features</p>
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Explore Enabled</Label>
                <Switch checked={config.ai_copilot_enabled !== false} onCheckedChange={v => setConfig(c => ({ ...c, ai_copilot_enabled: v }))} data-testid="config-ai-enabled" />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Embedding Enabled</Label>
                <Switch checked={config.embedding_enabled || false} onCheckedChange={v => setConfig(c => ({ ...c, embedding_enabled: v }))} />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Query Timeout (seconds)</Label>
                <Input type="number" value={config.query_timeout_seconds || 30} onChange={e => setConfig(c => ({ ...c, query_timeout_seconds: Number(e.target.value) }))}
                  className="w-24 h-7 text-xs rounded-lg bg-muted/50 border-border/50 text-right" />
              </div>
            </div>
          </div>

          <Button onClick={saveConfig} disabled={saving} className="rounded-xl bg-primary text-primary-foreground active:scale-95 transition-transform" data-testid="save-config-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Configuration
          </Button>

          {/* Explore Usage Analytics */}
          {exploreStats && (
            <div className="glass-card rounded-2xl p-6 space-y-4 mt-6" data-testid="explore-analytics">
              <h2 className="text-base font-heading font-semibold text-foreground">Explore Analytics</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-primary/5 border border-primary/10 text-center">
                  <p className="text-2xl font-bold text-foreground">{exploreStats.total_sessions}</p>
                  <p className="text-xs text-muted-foreground">Total Sessions</p>
                </div>
                <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/10 text-center">
                  <p className="text-2xl font-bold text-foreground">{exploreStats.total_findings}</p>
                  <p className="text-xs text-muted-foreground">Pinned Findings</p>
                </div>
              </div>
              {exploreStats.model_usage?.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Most Used Models</p>
                  {exploreStats.model_usage.map((m, i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-1">
                      <span className="text-foreground/70">{m.name || m.model_id}</span>
                      <span className="text-muted-foreground">{m.count} sessions</span>
                    </div>
                  ))}
                </div>
              )}
              {exploreStats.recent_sessions?.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Recent Sessions</p>
                  {exploreStats.recent_sessions.slice(0, 5).map((s, i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-1">
                      <span className="text-foreground/70">{s.user_name || s.user_id}</span>
                      <span className="text-muted-foreground/70 text-[10px]">{s.model_name || ''}</span>
                      <span className="text-muted-foreground">{s.updated_at ? new Date(s.updated_at).toLocaleDateString() : ''}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      </>}

      {/* Security Tab - Row-Level Security */}
      {adminTab === 'security' && (
        <div className="space-y-4">
          <div className="glass-card rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-base font-heading font-semibold text-foreground">Row-Level Security Policies</h2>
                <p className="text-xs text-muted-foreground">Control which data rows users can see based on their role or attributes.</p>
              </div>
              <Button onClick={() => setRlsFormOpen(true)} className="gap-1.5 rounded-lg" size="sm"><Plus className="w-3.5 h-3.5" /> New Policy</Button>
            </div>

            {/* RLS Create/Edit Form */}
            {rlsFormOpen && (
              <div className="p-4 rounded-xl border border-primary/20 bg-primary/5 mb-4 space-y-3">
                <h3 className="text-xs font-semibold text-primary">{editingRls ? 'Edit Policy' : 'New RLS Policy'}</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[10px] text-muted-foreground">Policy Name</Label><Input value={rlsForm.name} onChange={e => setRlsForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. LOB Filter for Underwriters" className="mt-0.5 text-sm text-foreground" /></div>
                  <div><Label className="text-[10px] text-muted-foreground">Model</Label>
                    <Select value={rlsForm.model_id} onValueChange={v => setRlsForm(p => ({ ...p, model_id: v }))}>
                      <SelectTrigger className="mt-0.5"><SelectValue placeholder="Select model..." /></SelectTrigger>
                      <SelectContent>{models.map(m => <SelectItem key={m.model_id} value={m.model_id}>{m.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label className="text-[10px] text-muted-foreground">Field</Label><Input value={rlsForm.field} onChange={e => setRlsForm(p => ({ ...p, field: e.target.value }))} placeholder="e.g. line_of_business" className="mt-0.5 text-sm text-foreground" /></div>
                  <div><Label className="text-[10px] text-muted-foreground">Operator</Label>
                    <Select value={rlsForm.operator} onValueChange={v => setRlsForm(p => ({ ...p, operator: v }))}>
                      <SelectTrigger className="mt-0.5"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="equals">Equals</SelectItem><SelectItem value="not_equals">Not Equals</SelectItem><SelectItem value="in">In (list)</SelectItem><SelectItem value="contains">Contains</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-[10px] text-muted-foreground">Value Source</Label>
                    <Select value={rlsForm.value_source} onValueChange={v => setRlsForm(p => ({ ...p, value_source: v }))}>
                      <SelectTrigger className="mt-0.5"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="static">Static Value</SelectItem><SelectItem value="user_attribute">User Attribute</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {rlsForm.value_source === 'static' ? (
                    <div><Label className="text-[10px] text-muted-foreground">Value</Label><Input value={rlsForm.value} onChange={e => setRlsForm(p => ({ ...p, value: e.target.value }))} placeholder="e.g. Auto" className="mt-0.5 text-sm text-foreground" /></div>
                  ) : (
                    <div><Label className="text-[10px] text-muted-foreground">User Attribute</Label><Input value={rlsForm.user_attribute} onChange={e => setRlsForm(p => ({ ...p, user_attribute: e.target.value }))} placeholder="e.g. department" className="mt-0.5 text-sm text-foreground" /></div>
                  )}
                  <div><Label className="text-[10px] text-muted-foreground">Roles (comma-separated, or blank for all)</Label><Input value={rlsForm.roles} onChange={e => setRlsForm(p => ({ ...p, roles: e.target.value }))} placeholder="e.g. viewer, editor" className="mt-0.5 text-sm text-foreground" /></div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={async () => {
                    const payload = { name: rlsForm.name, model_id: rlsForm.model_id, field: rlsForm.field, operator: rlsForm.operator, value_source: rlsForm.value_source, value: rlsForm.value_source === 'static' ? rlsForm.value : '', user_attribute: rlsForm.value_source === 'user_attribute' ? rlsForm.user_attribute : '', roles: rlsForm.roles ? rlsForm.roles.split(',').map(r => r.trim()).filter(Boolean) : [], enabled: true };
                    try {
                      if (editingRls) {
                        await api.updateRlsPolicy(editingRls.policy_id, payload);
                        setRlsPolicies(prev => prev.map(p => p.policy_id === editingRls.policy_id ? { ...p, ...payload } : p));
                        toast.success('Policy updated');
                      } else {
                        const p = await api.createRlsPolicy(payload);
                        setRlsPolicies(prev => [...prev, p]);
                        toast.success('Policy created');
                      }
                      setRlsFormOpen(false);
                      setEditingRls(null);
                      setRlsForm({ name: '', model_id: '', field: '', operator: 'equals', value_source: 'static', value: '', user_attribute: '', roles: '' });
                    } catch (err) { toast.error(err.message); }
                  }} className="rounded-lg" size="sm">{editingRls ? 'Update' : 'Create'} Policy</Button>
                  <Button variant="ghost" size="sm" onClick={() => { setRlsFormOpen(false); setEditingRls(null); }}>Cancel</Button>
                </div>
              </div>
            )}

            {rlsPolicies.length === 0 && !rlsFormOpen ? (
              <p className="text-xs text-muted-foreground/70 italic py-4">No RLS policies configured. Click "New Policy" to create one.</p>
            ) : rlsPolicies.map(p => (
              <div key={p.policy_id} className="flex items-center justify-between p-3 rounded-lg border border-border/30 mb-2 group">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{p.name}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {p.model_id && <span className="mr-2">Model: {models.find(m => m.model_id === p.model_id)?.name || p.model_id}</span>}
                    Field: <span className="font-mono">{p.field}</span> {p.operator} {p.value_source === 'user_attribute' ? <span className="text-primary">user.{p.user_attribute}</span> : <span className="font-mono">{p.value}</span>}
                    {p.roles?.length > 0 && <span className="ml-2">Roles: {p.roles.join(', ')}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={p.enabled !== false ? 'default' : 'outline'} className="text-[8px]">{p.enabled !== false ? 'Active' : 'Disabled'}</Badge>
                  <button onClick={() => { setEditingRls(p); setRlsForm({ name: p.name || '', model_id: p.model_id || '', field: p.field || '', operator: p.operator || 'equals', value_source: p.value_source || 'static', value: p.value || '', user_attribute: p.user_attribute || '', roles: (p.roles || []).join(', ') }); setRlsFormOpen(true); }} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-primary"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={async () => { await api.deleteRlsPolicy(p.policy_id); setRlsPolicies(prev => prev.filter(pp => pp.policy_id !== p.policy_id)); toast.success('Policy deleted'); }} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* API Keys Tab */}
      {adminTab === 'api_keys' && (
        <div className="space-y-4">
          {/* One-time plaintext key surface — shown immediately after
              creation, then dismissable. The server never returns this
              value again; if the user navigates away without copying it,
              they must mint a new key. */}
          {justCreatedKey && (
            <div className="glass-card rounded-2xl p-5 border border-amber-500/40 bg-amber-500/[0.04]" data-testid="just-created-key-banner">
              <p className="text-[10px] uppercase tracking-wider text-amber-400 font-semibold mb-1">Copy this key now — you won&apos;t see it again</p>
              <p className="text-xs text-foreground/70 mb-3">
                The full plaintext is shown here once. We only store a bcrypt hash on the server, so this is your single chance to capture it before it&apos;s gone.
              </p>
              <div className="flex items-center gap-2 mb-3">
                <code className="flex-1 px-3 py-2 rounded-lg bg-background/80 border border-border/40 font-mono text-xs text-foreground break-all">{justCreatedKey.plaintext_key}</code>
                <Button
                  onClick={() => { navigator.clipboard.writeText(justCreatedKey.plaintext_key); toast.success('Copied'); }}
                  className="rounded-lg"
                  data-testid="copy-new-key-btn"
                >Copy</Button>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setJustCreatedKey(null)} className="text-xs">Done</Button>
            </div>
          )}

          <div className="glass-card rounded-2xl p-6">
            <h2 className="text-base font-heading font-semibold text-foreground mb-1">API Keys</h2>
            <p className="text-xs text-muted-foreground mb-4">
              Long-lived tokens for headless data access (Excel, Power BI, Tableau, scripts). Each key carries the <code className="text-[10px]">data:read</code> scope. Stored as a bcrypt hash; the plaintext is shown once.
            </p>
            <div className="flex gap-2 mb-4">
              <Input value={newKeyName} onChange={e => setNewKeyName(e.target.value)} placeholder="Key name (e.g. Excel desktop)" className="flex-1 text-sm text-foreground" data-testid="api-key-name-input" />
              <Button
                onClick={async () => {
                  if (!newKeyName.trim()) return;
                  try {
                    const k = await api.createApiKey({ name: newKeyName });
                    // Modern shape: { key_id, name, scopes, plaintext_key, prefix, ... }
                    // Old shape:    { key_id, name, key, ... }
                    if (k.plaintext_key || k.key) {
                      setJustCreatedKey({
                        ...k,
                        plaintext_key: k.plaintext_key || k.key,
                      });
                    }
                    const listShape = {
                      ...k,
                      // Replace any leftover plaintext on the list row so it never gets shown twice.
                      key: undefined, plaintext_key: undefined,
                      prefix: k.prefix || (k.key ? `${k.key.slice(0, 11)}` : ''),
                    };
                    setApiKeys(prev => [listShape, ...prev]);
                    setNewKeyName('');
                  } catch (e) { toast.error(e.message); }
                }}
                className="rounded-lg"
                data-testid="create-api-key-btn"
              ><Plus className="w-4 h-4 mr-1" /> Create Key</Button>
            </div>
            {apiKeys.map(k => (
              <div key={k.key_id} className="flex items-center justify-between p-3 rounded-lg border border-border/30 mb-2 group" data-testid={`api-key-row-${k.key_id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">{k.name}</p>
                  <p className="text-[10px] text-muted-foreground font-mono">{k.prefix || k.key_masked || '***'}…</p>
                  <p className="text-[10px] text-muted-foreground/70">
                    {(k.scopes || ['data:read']).join(', ')}
                    {k.last_used_at && <span> · last used {new Date(k.last_used_at).toLocaleDateString()}</span>}
                  </p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      await api.revokeApiKey(k.key_id);
                      setApiKeys(prev => prev.filter(kk => kk.key_id !== k.key_id));
                      toast.success('Key revoked');
                    } catch (e) { toast.error(e.message); }
                  }}
                  className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
                  data-testid={`revoke-api-key-${k.key_id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {apiKeys.length === 0 && <p className="text-xs text-muted-foreground/70 italic">No API keys created.</p>}
          </div>

          {/* OData "Analyze in Model" helper — gives the user the exact URL
              to paste into Excel / Power BI / Tableau Data Source. */}
          <div className="glass-card rounded-2xl p-6" data-testid="odata-helper">
            <h2 className="text-base font-heading font-semibold text-foreground mb-1 flex items-center gap-2">
              <ExternalLink className="w-4 h-4 text-primary" />
              Analyze in Excel / Power BI / Tableau
            </h2>
            <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
              Inzara exposes every semantic model as an OData v4 feed. In Excel pick
              <strong className="text-foreground"> Data → Get Data → From Other Sources → From OData Feed</strong>, paste the URL below, and authenticate with the API key above (Basic auth — username can be anything, password is the key).
            </p>
            {odataModelOptions.length > 0 ? (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <Label className="text-[10px] text-muted-foreground">Semantic model:</Label>
                  <select
                    value={odataModelId}
                    onChange={e => setOdataModelId(e.target.value)}
                    className="text-xs rounded-md bg-muted/30 border border-border/40 px-2 py-1 text-foreground"
                    data-testid="odata-model-select"
                  >
                    {odataModelOptions.map(m => <option key={m.model_id} value={m.model_id}>{m.name}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 rounded-lg bg-background/80 border border-border/40 font-mono text-[11px] text-foreground break-all" data-testid="odata-url">
                    {process.env.REACT_APP_BACKEND_URL}/api/odata/v4/{odataModelId}
                  </code>
                  <Button
                    onClick={async () => {
                      // Smoke-test the URL using the current session cookie
                      // (NOT the API key — the user is here in the browser,
                      // already authenticated). If this succeeds, the URL
                      // is reachable and the user's tenant can see the
                      // model, which is what they care about.
                      try {
                        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/odata/v4/${odataModelId}`, { credentials: 'include' });
                        if (!res.ok) throw new Error(`HTTP ${res.status}`);
                        const doc = await res.json();
                        const setCount = (doc.value || []).length;
                        toast.success(`Connected — ${setCount} entity set${setCount === 1 ? '' : 's'} reachable`);
                      } catch (err) {
                        toast.error(`Connection failed: ${err.message}`);
                      }
                    }}
                    variant="outline"
                    className="rounded-lg text-xs"
                    data-testid="test-odata-btn"
                    title="Smoke-test that the URL is reachable from your tenant"
                  >Test</Button>
                  <Button
                    onClick={() => { navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/odata/v4/${odataModelId}`); toast.success('OData URL copied'); }}
                    variant="outline"
                    className="rounded-lg text-xs"
                    data-testid="copy-odata-url-btn"
                  >Copy</Button>
                </div>
                <p className="text-[10px] text-muted-foreground/70 mt-2">
                  Supports <code>$top</code>, <code>$skip</code>, <code>$select</code>, <code>$count</code>, and equality <code>$filter</code> (e.g. <code>?$filter=state eq 'California'</code>).
                </p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground/80 italic">Create a semantic model first to enable OData access.</p>
            )}
          </div>
        </div>
      )}

      {/* Audit Log Tab */}
      {adminTab === 'audit' && (
        <div className="space-y-4">
          <div className="glass-card rounded-2xl p-6">
            <h2 className="text-base font-heading font-semibold text-foreground mb-4">Audit Log</h2>
            {auditLogs.length === 0 ? (
              <p className="text-xs text-muted-foreground/70 italic py-4">No audit logs found.</p>
            ) : (
              <div className="space-y-1 max-h-96 overflow-auto">
                {auditLogs.slice(0, 50).map((log, i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted/20 text-xs">
                    <span className="text-muted-foreground/70 w-32 flex-shrink-0">{log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}</span>
                    <span className="text-foreground/70 w-24 flex-shrink-0">{log.user_name || log.user_id}</span>
                    <Badge variant="outline" className="text-[7px] w-16 justify-center flex-shrink-0">{log.action}</Badge>
                    <span className="text-muted-foreground truncate">{log.resource_type} {log.resource_id} {typeof log.details === 'object' ? JSON.stringify(log.details) : (log.details || '')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Branding Tab */}
      {adminTab === 'branding' && (
        <div className="space-y-4">
          <div className="glass-card rounded-2xl p-6 space-y-4">
            <h2 className="text-base font-heading font-semibold text-foreground">Custom Branding</h2>
            <p className="text-xs text-muted-foreground">Customize the look and feel for your organization.</p>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs text-muted-foreground">App Name</Label><Input value={branding.app_name || ''} onChange={e => setBranding(p => ({ ...p, app_name: e.target.value }))} placeholder="INZARA" className="mt-1 text-foreground" /></div>
              <div><Label className="text-xs text-muted-foreground">Upload Logo</Label><div className="flex gap-2 mt-1 items-center">{branding.logo_url && <img src={branding.logo_url} alt="" className="h-8 object-contain rounded border border-border/30" />}<label className="px-3 py-1.5 rounded-lg border border-border/30 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/30 cursor-pointer">Choose File<input type="file" accept="image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) { const r = new FileReader(); r.onload = () => setBranding(p => ({ ...p, logo_url: r.result })); r.readAsDataURL(f); } }} /></label><Input value={branding.logo_url || ''} onChange={e => setBranding(p => ({ ...p, logo_url: e.target.value }))} placeholder="or paste URL" className="flex-1 text-foreground text-xs" /></div></div>
              <div><Label className="text-xs text-muted-foreground">Primary Color</Label><div className="flex gap-2 mt-1"><input type="color" value={branding.primary_color || '#3b82f6'} onChange={e => setBranding(p => ({ ...p, primary_color: e.target.value }))} className="w-10 h-8 rounded cursor-pointer" /><Input value={branding.primary_color || '#3b82f6'} onChange={e => setBranding(p => ({ ...p, primary_color: e.target.value }))} className="flex-1 text-foreground font-mono" /></div></div>
              <div><Label className="text-xs text-muted-foreground">Accent Color</Label><div className="flex gap-2 mt-1"><input type="color" value={branding.accent_color || '#10b981'} onChange={e => setBranding(p => ({ ...p, accent_color: e.target.value }))} className="w-10 h-8 rounded cursor-pointer" /><Input value={branding.accent_color || '#10b981'} onChange={e => setBranding(p => ({ ...p, accent_color: e.target.value }))} className="flex-1 text-foreground font-mono" /></div></div>
              <div><Label className="text-xs text-muted-foreground">Upload Favicon</Label><div className="flex gap-2 mt-1 items-center">{branding.favicon_url && <img src={branding.favicon_url} alt="" className="h-6 w-6 object-contain rounded border border-border/30" />}<label className="px-3 py-1.5 rounded-lg border border-border/30 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/30 cursor-pointer">Choose File<input type="file" accept="image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) { const r = new FileReader(); r.onload = () => setBranding(p => ({ ...p, favicon_url: r.result })); r.readAsDataURL(f); } }} /></label><Input value={branding.favicon_url || ''} onChange={e => setBranding(p => ({ ...p, favicon_url: e.target.value }))} placeholder="or paste URL" className="flex-1 text-foreground text-xs" /></div></div>
              <div><Label className="text-xs text-muted-foreground">Sidebar Background</Label><div className="flex gap-2 mt-1"><input type="color" value={branding.sidebar_bg || '#09090b'} onChange={e => setBranding(p => ({ ...p, sidebar_bg: e.target.value }))} className="w-10 h-8 rounded cursor-pointer" /><Input value={branding.sidebar_bg || '#09090b'} onChange={e => setBranding(p => ({ ...p, sidebar_bg: e.target.value }))} className="flex-1 text-foreground font-mono" /></div></div>
            </div>
            <Button onClick={async () => { try { await api.updateBranding(branding); toast.success('Branding saved'); } catch (err) { toast.error(err.message); } }} className="rounded-lg"><Save className="w-4 h-4 mr-1" /> Save Branding</Button>
          </div>
        </div>
      )}
    </div>
  );
}
